import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Linkedin, Twitter, Link2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eyebrow, Reveal, GoldButton, MaskedLines, EASE } from "@/components/site/motion";
import { api, mediaUrl } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";

const INLINE_RE = /(\*\*[^*]+\*\*)|(\[([^\]]+)\]\((https?:\/\/[^)\s]+)\))/g;

function renderInline(text) {
  const out = [];
  let last = 0;
  let k = 0;
  let m;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) {
      out.push(
        <strong key={k++} className="font-bold text-ink">
          {m[1].slice(2, -2)}
        </strong>
      );
    } else {
      out.push(
        <a
          key={k++}
          href={m[4]}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-gold underline decoration-gold/40 underline-offset-4 transition-colors duration-300 hover:decoration-gold"
        >
          {m[3]}
        </a>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function InsightArticle() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api
      .insight(id)
      .then((r) => {
        setPost(r.data);
        setState("ok");
      })
      .catch(() => setState("missing"));
  }, [id]);

  const pageUrl = window.location.href;
  const shareImage = post?.image
    ? post.image.startsWith("/api/")
      ? mediaUrl(post.image)
      : post.image.startsWith("/")
        ? `${window.location.origin}${post.image}`
        : post.image
    : undefined;

  useSEO(
    post ? `${post.title} — Hapo Group` : "Insights — Hapo Group",
    post?.title || "The latest news, perspectives and events from across the Hapo Group.",
    { image: shareImage, url: pageUrl, type: post ? "article" : "website" }
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      toast.success("Link copied to clipboard.");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  if (state === "loading") {
    return (
      <section className="flex min-h-screen items-center justify-center bg-ink" data-testid="insight-article-loading">
        <p className="animate-pulse text-[11px] font-semibold uppercase tracking-[0.3em] text-smoke">Loading…</p>
      </section>
    );
  }

  if (state === "missing") {
    return (
      <section className="flex min-h-screen flex-col items-center justify-center bg-ink px-6 text-center" data-testid="insight-article-missing">
        <Eyebrow className="justify-center">Insights</Eyebrow>
        <h1 className="mt-6 text-4xl font-extrabold tracking-tighter text-white sm:text-5xl">Article not found.</h1>
        <p className="mt-6 max-w-md text-sm leading-loose text-smoke">
          This article may have been removed. Browse the latest from across the group instead.
        </p>
        <div className="mt-10">
          <GoldButton to="/insights" testId="insight-missing-home">
            Browse Insights
          </GoldButton>
        </div>
      </section>
    );
  }

  const blocks = (post.body || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <article data-testid="insight-article">
      <section className="relative flex items-end overflow-hidden bg-ink pt-44 pb-20 lg:pb-28" aria-label={post.title}>
        <div className="w-full px-6 lg:px-12">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <Link
              to="/insights"
              data-testid="back-to-insights"
              className="group inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-smoke transition-colors duration-300 hover:text-gold"
            >
              <ArrowLeft className="h-3.5 w-3.5 text-gold transition-transform duration-300 group-hover:-translate-x-1" />
              Back to Insights
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }} className="mt-10">
            <Eyebrow>{post.tag || post.category}</Eyebrow>
          </motion.div>
          <MaskedLines
            as="h1"
            lines={[post.title]}
            delay={0.2}
            className="mt-5 max-w-5xl text-4xl font-extrabold leading-[1.02] tracking-tighter text-white sm:text-5xl lg:text-7xl"
            data-testid="insight-article-title"
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: EASE }}
            className="mt-8 text-[11px] font-semibold uppercase tracking-[0.24em] text-smoke"
          >
            {post.category}
            {post.date ? ` · ${post.date}` : ""}
          </motion.p>
        </div>
      </section>

      {post.image && (
        <section className="bg-ink px-6 pb-20 lg:px-12" aria-hidden="true">
          <Reveal className="overflow-hidden">
            <motion.img
              initial={{ scale: 1.08 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.4, ease: EASE }}
              src={mediaUrl(post.image)}
              alt=""
              className="aspect-[21/9] w-full object-cover"
            />
          </Reveal>
        </section>
      )}

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[120px]" aria-label="Article body">
        <div className="mx-auto max-w-3xl" data-testid="insight-article-body">
          {blocks.length > 0 ? (
            blocks.map((b, i) => {
              const lines = b.split("\n").filter((l) => l.trim());
              if (lines.length > 0 && lines.every((l) => l.trim().startsWith("- "))) {
                return (
                  <Reveal key={i} delay={Math.min(i * 0.05, 0.3)} className={i > 0 ? "mt-8" : ""}>
                    <ul className="space-y-3">
                      {lines.map((l, j) => (
                        <li key={j} className="flex gap-3 text-base leading-loose text-ink/80 md:text-lg">
                          <span className="mt-[0.75em] h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" />
                          <span>{renderInline(l.trim().slice(2))}</span>
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                );
              }
              const imgMatch = b.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
              if (imgMatch) {
                return (
                  <Reveal key={i} delay={Math.min(i * 0.05, 0.3)} className={i > 0 ? "mt-10" : ""}>
                    <figure>
                      <img
                        src={mediaUrl(imgMatch[2])}
                        alt={imgMatch[1] || ""}
                        loading="lazy"
                        className="w-full object-cover"
                      />
                      {imgMatch[1] && imgMatch[1] !== "image" && (
                        <figcaption className="mt-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-smoke">
                          {imgMatch[1]}
                        </figcaption>
                      )}
                    </figure>
                  </Reveal>
                );
              }
              if (b.startsWith("## ")) {
                return (
                  <Reveal key={i} delay={Math.min(i * 0.05, 0.3)} className={i > 0 ? "mt-10" : ""}>
                    <h2 className="text-2xl font-bold tracking-tight text-ink md:text-3xl">
                      {renderInline(b.slice(3))}
                    </h2>
                  </Reveal>
                );
              }
              return (
                <Reveal key={i} delay={Math.min(i * 0.05, 0.3)} className={i > 0 ? "mt-8" : ""}>
                  <p className="whitespace-pre-line text-base leading-loose text-ink/80 md:text-lg md:leading-loose">
                    {renderInline(b)}
                  </p>
                </Reveal>
              );
            })
          ) : (
            <p className="text-base leading-loose text-ink/80 md:text-lg">
              More details on this story are coming soon. For anything urgent, get in touch with the group directly.
            </p>
          )}
        </div>
        <div className="mx-auto mt-16 max-w-3xl border-t border-ink/10 pt-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-smoke">Share this article</p>
          <div className="mt-5 flex gap-3">
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on LinkedIn"
              data-testid="share-linkedin"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 text-ink/60 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <Linkedin className="h-4 w-4" />
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on X"
              data-testid="share-x"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 text-ink/60 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <Twitter className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={copyLink}
              aria-label="Copy link"
              data-testid="share-copy-link"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 text-ink/60 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <Link2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <section className="bg-ink px-6 py-24 text-center lg:px-12" aria-label="More from Hapo Group">
        <Reveal>
          <Eyebrow className="justify-center">Hapo Group</Eyebrow>
          <h2 className="mx-auto mt-6 max-w-3xl text-3xl font-extrabold leading-tight tracking-tighter text-white sm:text-4xl">
            Want to know more about the group?
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <GoldButton to="/contact" testId="insight-article-contact">
              Get in Touch
            </GoldButton>
          </div>
        </Reveal>
      </section>
    </article>
  );
}
